// features/findings/charts/tornado-plot.js
// Tornado / sensitivity plot — horizontal bars from a center axis.
// Each row: one sensitivity dimension. Bar to left = effect under one
// classification; bar to right = under the alternative.

export function TornadoPlot({ title, baselineLabel, dimensions }) {
  if (!dimensions || dimensions.length === 0) return null;

  // Sort by absolute combined effect descending
  const sorted = [...dimensions].sort((a, b) => {
    const ea = Math.abs(a.low) + Math.abs(a.high);
    const eb = Math.abs(b.low) + Math.abs(b.high);
    return eb - ea;
  });

  const W = 720;
  const rowH = 36;
  const H = 60 + sorted.length * rowH;
  const ML = 200;
  const MR = 40;
  const MT = 36;
  const innerW = W - ML - MR;
  const center = ML + innerW / 2;

  const max = Math.max(
    1,
    ...sorted.flatMap((d) => [Math.abs(d.low), Math.abs(d.high)])
  );

  return (
    <figure className="finding-chart">
      <figcaption className="finding-chart-caption">{title}</figcaption>
      <div className="finding-svg-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="finding-svg" role="img" aria-label={title}>
          {/* Center axis */}
          <line x1={center} y1={MT - 8} x2={center} y2={H - 12} className="finding-svg-axis" />
          {/* Baseline label */}
          <text x={center} y={MT - 14} className="finding-svg-axis-label" textAnchor="middle">
            {baselineLabel || 'baseline'}
          </text>

          {sorted.map((d, i) => {
            const y = MT + i * rowH + rowH / 2;
            const lowW = (Math.abs(d.low) / max) * (innerW / 2 - 8);
            const highW = (Math.abs(d.high) / max) * (innerW / 2 - 8);
            return (
              <g key={d.id}>
                {/* Dim label */}
                <text x={ML - 12} y={y} className="finding-svg-axis-label" textAnchor="end" dominantBaseline="middle">
                  {d.label}
                </text>
                {/* Low bar (left) */}
                {d.low < 0 && (
                  <rect
                    x={center - lowW} y={y - 10} width={lowW} height={20}
                    className="finding-tornado-bar finding-tornado-low"
                  />
                )}
                {d.low > 0 && (
                  <rect
                    x={center} y={y - 10} width={lowW} height={20}
                    className="finding-tornado-bar finding-tornado-low"
                  />
                )}
                {/* High bar (right) */}
                {d.high > 0 && (
                  <rect
                    x={center} y={y - 10} width={highW} height={20}
                    className="finding-tornado-bar finding-tornado-high"
                  />
                )}
                {d.high < 0 && (
                  <rect
                    x={center - highW} y={y - 10} width={highW} height={20}
                    className="finding-tornado-bar finding-tornado-high"
                  />
                )}
                {/* Value labels */}
                <text x={center - lowW - 4} y={y} className="finding-svg-axis-label" textAnchor="end" dominantBaseline="middle">
                  {d.lowLabel ?? d.low}
                </text>
                <text x={center + highW + 4} y={y} className="finding-svg-axis-label" textAnchor="start" dominantBaseline="middle">
                  {d.highLabel ?? d.high}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="finding-tornado-legend">
        <span><span className="finding-tornado-swatch finding-tornado-low" /> alternative classification (lower)</span>
        <span><span className="finding-tornado-swatch finding-tornado-high" /> alternative classification (higher)</span>
      </div>
    </figure>
  );
}

export default TornadoPlot;
